import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardContent,
  Collapse,
  Grid,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Skeleton,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useContentMetadata,
  useContentStatuses,
} from "../hooks/useCustomQuery";
import CloseIcon from "@mui/icons-material/Close";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import LanguageIcon from "@mui/icons-material/Language";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ContentCardActions from "./ContentCardActions";
import { getStatusOptions, getYear } from "../utils/utils";
import useResizeObserver from "../hooks/useResizeObserver";
import PosterTile from "./PosterTile";
import { useQueryClient } from "@tanstack/react-query";
import { ExpandableTypography } from "./ExpandableTypography";
import WatchProviders from "./WatchProviders";
import { useAuth } from "../context/authContext";

const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

function TVShowCard({
  contentType,
  contentId,
  onContentChange,
  onClose,
  status,
  onStatusChange,
  fullscreen,
  statusMenuOpenSignal,
  collapsed = false, // mobile only: shrinks to the poster + title/person/year
  // (see LibraryModal) when the tab content below has been scrolled
}) {
  const queryClient = useQueryClient();
  const { data: metadata, isLoading } = useContentMetadata(
    contentType,
    contentId,
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeSeasonNumber, setActiveSeasonNumber] = useState(0);
  const [isPersonOverflowing, setIsPersonOverflowing] = useState(false);
  const personRef = useRef(null);

  const { width: directorWidth, height: directorHeight } =
    useResizeObserver(personRef);

  const { user } = useAuth();
  const statusOptions = getStatusOptions(theme);

  const seriesContentId = metadata?.seasons[0]?.content_id;
  const activeSeason =
    metadata?.seasons?.find((s) => s.season_number === activeSeasonNumber) ??
    metadata?.seasons?.[0];

  // Fetched up front (not just for the active tab) so every season tab can
  // show its own status icon at a glance. All season ids coalesce into a
  // single /statuses/batch request via useContentStatuses.
  const numberedSeasons =
    metadata?.seasons?.filter((s) => s.season_number > 0) ?? [];
  const seasonContentIds = numberedSeasons.map((s) => s.content_id);
  const seasonStatuses = useContentStatuses(seasonContentIds, {
    enabled: !!user,
  });

  // On the "All" tab, `status` (from the parent) is keyed by the series
  // content id, so it already reflects the backend-derived series status.
  const queueTarget = numberedSeasons.find(
    (s) => seasonStatuses[s.content_id] === "none",
  );
  const startTarget =
    numberedSeasons.find((s) => seasonStatuses[s.content_id] === "queued") ??
    queueTarget;
  const finishTarget = numberedSeasons.find(
    (s) => seasonStatuses[s.content_id] !== "finished",
  );

  const seriesCommands = [
    queueTarget && {
      label: `Queue S${queueTarget.season_number}`,
      value: "queueSeason",
      newStatus: "queued",
      targetContentId: queueTarget.content_id,
      targetCurrentStatus: seasonStatuses[queueTarget.content_id],
      icon: statusOptions.queued.iconFilled,
      color: statusOptions.queued.color,
      navigateToTarget: true,
    },
    startTarget && {
      label: `Start S${startTarget.season_number}`,
      value: "startSeason",
      newStatus: "started",
      targetContentId: startTarget.content_id,
      targetCurrentStatus: seasonStatuses[startTarget.content_id],
      icon: statusOptions.started.iconFilled,
      color: statusOptions.started.color,
      navigateToTarget: true,
    },
    finishTarget && {
      label: `Finish S${finishTarget.season_number}`,
      value: "finishSeason",
      newStatus: "finished",
      targetContentId: finishTarget.content_id,
      targetCurrentStatus: seasonStatuses[finishTarget.content_id],
      icon: statusOptions.finished.iconFilled,
      color: statusOptions.finished.color,
      navigateToTarget: true,
    },
    status !== "finished"
      ? {
          label: "Finish series",
          value: "finishAllSeasons",
          newStatus: "finished",
          targetContentId: seriesContentId,
          targetCurrentStatus: status,
          icon: statusOptions.finished.iconFilled,
          color: statusOptions.finished.color,
        }
      : {
          // Mirrors the general dropdown's "Log again": re-logs a finished
          // series, creating a journal entry at the series level and
          // re-propagating "finished" down to every season.
          label: (
            <>
              Finish series <br /> again
            </>
          ),
          value: "logAllSeasonsFinishedAgain",
          newStatus: "logAgain",
          targetContentId: seriesContentId,
          targetCurrentStatus: status,
          icon: statusOptions.finished.iconFilled,
          color: statusOptions.finished.color,
        },
    status !== "abandoned" && {
      label: "Stop series",
      value: "stopSeries",
      newStatus: "abandoned",
      targetContentId: seriesContentId,
      targetCurrentStatus: status,
      icon: statusOptions.abandoned.iconFilled,
      color: statusOptions.abandoned.color,
    },
    status !== "none" && {
      label: (
        <>
          Clear series <br /> tracking
        </>
      ),
      value: "none",
      newStatus: "none",
      targetContentId: seriesContentId,
      targetCurrentStatus: status,
      icon: <HighlightOffIcon />,
      color: "#bdbdbd",
      // delete_status detects this is a series-wide clear from the content id
      // alone and reports seriesWide/clearedContentIds back, so LibraryModal
      // can pick the right confirm wording and purge the right caches without
      // this command needing to say anything more than "clear the series".
    },
  ].filter(Boolean);

  // Wraps the lifted onStatusChange so series-tab commands resolve to the
  // season/series content id and current status they actually target,
  // rather than acting on whatever tab is currently active. Season-targeted
  // commands then jump the user to that season's tab so they can see (and
  // optionally fill in) the journal entry the status change just created.
  const handleSeriesCommandChange = async (commandValue) => {
    const command = seriesCommands.find((c) => c.value === commandValue);
    if (!command) return onStatusChange(commandValue);

    const result = await onStatusChange(command.newStatus, {
      contentId: command.targetContentId,
      currentStatus: command.targetCurrentStatus,
    });

    // result is falsy when handleStatusChange short-circuited (e.g. the user
    // isn't authenticated and just saw the auth dialog) rather than mutating
    if (command.navigateToTarget && result) {
      onContentChange(command.targetContentId);
    }

    return result;
  };

  useEffect(() => {
    // Derives which season is selected *based on navigation*
    if (!metadata) return;

    const matchedSeason = metadata.seasons.find(
      (s) => s.content_id === contentId,
    );

    setActiveSeasonNumber(matchedSeason?.season_number ?? 0);
  }, [metadata, contentId]);

  useEffect(() => {
    // Enforces domain rule: single-season actions always attach to S1
    if (!metadata) return;
    if (metadata.series.season_count !== 1) return;

    const season1 = metadata.seasons.find((s) => s.season_number === 1);
    if (season1) onContentChange(season1.content_id);
  }, [metadata, onContentChange]);

  useEffect(() => {
    // Remaps the content query so it's always keyed by series content id
    if (!metadata) return;
    if (!seriesContentId) return;
    if (seriesContentId === contentId) return;

    queryClient.setQueryData(["tvshow", "metadata", seriesContentId], metadata);
    // queryClient.removeQueries(["tvshow", "metadata", contentId]);
  }, [metadata, contentId, queryClient, seriesContentId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (personRef.current) {
        setIsPersonOverflowing(
          personRef.current.scrollWidth > personRef.current.offsetWidth,
        );
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [directorWidth, directorHeight, metadata?.series?.persons]);

  const originalLanguage = metadata?.series?.original_language
    ? languageNames.of(metadata.series.original_language)
    : "Unknown";

  const handleTabChange = (e, newIndex) => {
    const season = metadata?.seasons[newIndex];
    setActiveSeasonNumber(season.season_number);
    onContentChange(season.content_id);
  };

  const loadingDiv = (
    <Stack direction="column" spacing={1}>
      <Skeleton width="70%" />
      <Skeleton width="60%" />
      <Skeleton width="50%" />
    </Stack>
  );

  const titlePersonYearDiv = (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={1}
      alignItems="flex-start"
      sx={{ pr: 2 }}
    >
      <Box
        sx={{
          "& > span": {
            marginRight: 2,
          },
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{
            fontWeight: "bold",
            display: "inline-block",
          }}
        >
          {metadata?.series?.season_count > 1
            ? activeSeason?.title
            : metadata?.seasons[0].title}
        </Typography>
        <Box
          component="span"
          sx={{
            whiteSpace: "nowrap",
          }}
        >
          {metadata?.series?.persons?.length > 0 && (
            <Box sx={{ display: "inline-flex" }}>
              <Tooltip
                // Conditionally display tooltip with full name of director in the event it becomes clipped due to overflow
                title={metadata?.series?.persons.join(", ")}
                disableHoverListener={!isPersonOverflowing}
                placement="bottom-start"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -14],
                        },
                      },
                    ],
                  },
                }}
              >
                <Typography
                  ref={personRef}
                  variant="subtitle1"
                  component="span"
                  sx={{
                    marginRight: 2,
                    maxWidth: {
                      xs: "160px",
                      sm: "260px",
                      md: "360px",
                    },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {metadata?.series?.persons.join(", ")}
                </Typography>
              </Tooltip>
            </Box>
          )}
          <Typography
            variant="subtitle1"
            component="span"
            color="text.secondary"
            sx={{ display: "inline-block" }}
          >
            <i>{getYear(activeSeason?.air_date)}</i>
          </Typography>
        </Box>
      </Box>
    </Stack>
  );

  const genresDiv = (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ pt: "6px" }}>
        <TheaterComedyIcon fontSize="small" />
      </Box>
      <Box sx={{ flexWrap: "wrap" }}>
        {metadata?.series?.genres?.map((genre) => (
          <Chip
            key={genre}
            label={genre.toLowerCase()}
            size="small"
            variant="filled"
            sx={{
              "&.MuiChip-root": {
                mt: 0.5,
                mb: 0.5,
                mr: 1,
              },
            }}
          />
        ))}
      </Box>
    </Stack>
  );

  const tvStatsDiv = (
    <Box
      sx={{
        flexWrap: "wrap",
        display: "flex",
        direction: "row",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
          <SubscriptionsIcon fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            {activeSeasonNumber === 0
              ? `${metadata?.series?.season_count} ${metadata?.series?.season_count === 1 ? "season" : "seasons"}`
              : `${activeSeason?.episode_count} ${activeSeason?.episode_count === 1 ? "episode" : "episodes"}`}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
          <LanguageIcon fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            {originalLanguage}
          </Typography>
        </Stack>
      </Box>
      {genresDiv}
    </Box>
  );

  return (
    <>
      <Card
        sx={{
          display: "flex",
          position: "relative",
          width: "100%",
        }}
      >
        <CardContent
          sx={{
            padding: 2,
            width: "100%",
            "&:last-child": {
              paddingBottom: 2,
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <PosterTile
                contentId={contentId}
                contentType={contentType}
                contentTitle={activeSeason?.title}
                imageUrl={activeSeason?.image_url}
                inline
              />
              {isMobile && (
                <Collapse in={!collapsed}>
                  <Box sx={{ mt: 1 }}>
                    <WatchProviders
                      providers={metadata?.watch_providers}
                      checked={metadata?.watch_providers_checked}
                      isMobile
                    />
                  </Box>
                </Collapse>
              )}
            </Grid>
            <Grid
              item
              xs={9}
              position="relative"
              display="flex"
              flexDirection="column"
            >
              {isLoading ? (
                loadingDiv
              ) : (
                <>
                  <Box flexGrow={1}>
                    <Stack direction="column" spacing={1}>
                      {titlePersonYearDiv}
                      {/* Everything but the title/person/year collapses on
                          mobile once the tab content below is scrolled — see
                          the `collapsed` prop and LibraryModal. Unmounted once
                          hidden, so the overview's Show more resets to
                          collapsed when the card expands again. */}
                      <Collapse in={!collapsed} unmountOnExit>
                        <Stack direction="column" spacing={1}>
                          {metadata?.seasons &&
                            metadata?.series?.season_count > 1 && (
                              <Tabs
                                value={activeSeasonNumber}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons={false}
                                // allowScrollButtonsMobile
                                sx={{
                                  maxWidth: "100%",
                                  borderBottom: 1,
                                  borderColor: "divider",
                                  minHeight: 30,
                                }}
                              >
                                {metadata?.seasons?.map((season) => {
                                  const seasonStatus =
                                    seasonStatuses[season.content_id];
                                  const seasonStatusIcon =
                                    seasonStatus && seasonStatus !== "none" ? (
                                      statusOptions[seasonStatus]?.icon
                                    ) : (
                                      <CircleOutlinedIcon
                                        sx={{ color: "action.disabled" }}
                                      />
                                    );

                                  return (
                                    <Tab
                                      key={season.season_number}
                                      label={
                                        <Stack
                                          direction="row"
                                          spacing={0.5}
                                          alignItems="center"
                                          sx={{ height: 20 }}
                                        >
                                          {season.season_number !== 0 && (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                "& .MuiSvgIcon-root": {
                                                  fontSize: 16,
                                                },
                                              }}
                                            >
                                              {seasonStatusIcon}
                                            </Box>
                                          )}
                                          <span>
                                            {season.season_number === 0
                                              ? "All"
                                              : `S${season.season_number}`}
                                          </span>
                                        </Stack>
                                      }
                                      sx={{
                                        minWidth: 20,
                                        py: 0,
                                        minHeight: 30,
                                      }}
                                    />
                                  );
                                })}
                              </Tabs>
                            )}
                          {tvStatsDiv}

                          <ExpandableTypography text={activeSeason?.overview} />
                        </Stack>
                      </Collapse>
                    </Stack>
                    <Tooltip title="Close">
                      <IconButton
                        onClick={onClose}
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 0,
                          p: 0.5,
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Collapse in={!collapsed}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 2 }}
                    >
                      {isMobile ? (
                        <Box />
                      ) : (
                        <WatchProviders
                          providers={metadata?.watch_providers}
                          checked={metadata?.watch_providers_checked}
                        />
                      )}

                      <ContentCardActions
                        status={status}
                        onStatusChange={
                          activeSeasonNumber === 0
                            ? handleSeriesCommandChange
                            : onStatusChange
                        }
                        contentId={contentId}
                        contentType={contentType}
                        statusOptionsOverride={
                          activeSeasonNumber === 0 ? seriesCommands : undefined
                        }
                        // Only set on a real season tab of a multi-season show —
                        // single-season shows skip the tab bar entirely (see the
                        // season_count === 1 redirect effect above), so there's
                        // no ambiguity for "S1" to resolve there.
                        seasonNumber={
                          activeSeasonNumber !== 0 &&
                          metadata?.series?.season_count > 1
                            ? activeSeasonNumber
                            : undefined
                        }
                        openSignal={statusMenuOpenSignal}
                        statusMenuFooterText={
                          activeSeasonNumber === 0 && status === "abandoned" ? (
                            <>
                              Or update any season <br /> to resume tracking.
                            </>
                          ) : undefined
                        }
                      />
                    </Stack>
                  </Collapse>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}

export default TVShowCard;
